import styles from 'css/src/components/public-pages/contentful-components/audio-sync/AudioTrackItem.module.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AudioTrack } from './useMediaBunnyMixer';

import TrimmerAudioTimeline from 'js/src/components/common/asset-trimmer/TrimmerAudioTimeline';
import { BUTTON_SHAPES, MediaIconButton } from 'js/src/components/common/ui/Button';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import type { DropdownRowSliderProps } from 'js/src/components/common/ui/DropdownRowSlider';
import DropdownRowSlider from 'js/src/components/common/ui/DropdownRowSlider';
import { noop } from 'js/src/libs/consts';

type VolumeSliderItem = PlainDropdownItem<never> & Readonly<Partial<DropdownRowSliderProps>>;

interface AudioTrackItemProps {
    track: AudioTrack;
    totalDurationMs: number;
    timelineWidth: number;
    timelineStartMs: number;
    onOffsetChange: (id: string, offsetMs: number) => void;
    onRemove: (id: string) => void;
    onVolumeChange: (id: string, volume: number) => void;
}

const TRACK_COLORS = [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#ec4899',
    '#06b6d4',
    '#84cc16',
];

function getTrackColor(index: number): string {
    return TRACK_COLORS[index % TRACK_COLORS.length];
}

// Simple hash function to get consistent color per track id
function hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
    }
    return Math.abs(hash);
}

const AudioTrackItem: React.FC<AudioTrackItemProps> = ({
    track,
    totalDurationMs,
    timelineWidth,
    timelineStartMs,
    onOffsetChange,
    onRemove,
    onVolumeChange,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartOffset, setDragStartOffset] = useState(0);
    const trackRef = useRef<HTMLDivElement>(null);

    const color = useMemo(() => getTrackColor(hashCode(track.id)), [track.id]);

    const audioSourceUrl = useMemo(() => URL.createObjectURL(track.file), [track.file]);

    useEffect(() => {
        return () => {
            URL.revokeObjectURL(audioSourceUrl);
        };
    }, [audioSourceUrl]);

    // Calculate track width and position based on duration and offset
    const msPerPixel = totalDurationMs / timelineWidth;
    const trackWidthPx = track.durationMs / msPerPixel;
    const trackLeftPx = (track.offsetMs - timelineStartMs) / msPerPixel;

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if ((e.target as HTMLElement).closest('.dropdown')) {
                // don't start drag if clicking on a dropdown
                return;
            }
            // Only start drag on left mouse button
            if (e.button !== 0) return;

            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
            setDragStartX(e.clientX);
            setDragStartOffset(track.offsetMs);
        },
        [track.offsetMs],
    );

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const deltaX = e.clientX - dragStartX;
            const deltaMs = deltaX * msPerPixel;
            const newOffset = dragStartOffset + deltaMs;
            onOffsetChange(track.id, newOffset);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStartX, dragStartOffset, msPerPixel, track.id, onOffsetChange]);

    const handleRemove = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onRemove(track.id);
        },
        [track.id, onRemove],
    );

    const handleVolumeChange = useCallback(
        (volume: number) => {
            onVolumeChange(track.id, volume);
        },
        [track.id, onVolumeChange],
    );
    const volumeDropdownItems = useMemo(
        (): readonly VolumeSliderItem[] => [
            {
                id: 'volume-control',
                itemComponent: DropdownRowSlider,
                volumeValue: track.volume,
                onChange: handleVolumeChange,
            },
        ],
        [track.volume, handleVolumeChange],
    );

    const volumeToggleProps = useMemo(
        () => ({
            icon: track.volume > 0 ? 'volume_up' : 'volume_off',
            shape: BUTTON_SHAPES.SQUARE,
        }),
        [track.volume],
    );

    return (
        <div
            ref={trackRef}
            data-track-item
            className={`${styles['audio-track-item']} position-absolute d-flex flex-column overflow-hidden ${isDragging ? styles['dragging'] : ''}`}
            style={{
                left: `${trackLeftPx}px`,
                width: `${trackWidthPx}px`,
                backgroundColor: color,
                cursor: isDragging ? 'grabbing' : 'grab',
            }}
            onMouseDown={handleMouseDown}
        >
            <div
                className={`${styles['audio-track-header']} d-flex justify-content-between align-items-center`}
            >
                <span className={`${styles['audio-track-name']} d-flex align-items-center`}>
                    <Dropdown
                        breakOverflow={true}
                        ToggleComponent={MediaIconButton}
                        items={volumeDropdownItems}
                        toggleProps={volumeToggleProps}
                    />
                    {track.name}
                </span>
                <button
                    onClick={handleRemove}
                    type="button"
                    className={`${styles['audio-track-remove-btn']} d-flex align-items-center justify-content-center`}
                >
                    <i translate="no" className="material-symbols-outlined">
                        close
                    </i>
                </button>
            </div>

            <div className={styles['audio-track-waveform']}>
                <TrimmerAudioTimeline
                    source={audioSourceUrl}
                    onLoaded={noop}
                    onAudioDetected={noop}
                    className="audio-track-waveform-inner w-100 h-100"
                />
            </div>
        </div>
    );
};

export default React.memo(AudioTrackItem);
